import React from 'react';
import Icon from '../../../components/AppIcon';

const WeeklyOverview = ({ 
  exercises = [], 
  currentWeek = 1, 
  totalWeeks = 12,
  className = '' 
}) => {
  const completedCount = exercises?.filter(ex => ex?.completed)?.length;
  const totalCount = exercises?.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getStatusCounts = () => {
    const today = new Date();
    const counts = {
      completed: 0,
      overdue: 0,
      dueToday: 0,
      dueSoon: 0,
      upcoming: 0
    };

    exercises?.forEach(exercise => {
      if (exercise?.completed) {
        counts.completed++;
        return;
      }

      const dueDate = new Date(exercise.dueDate);
      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) counts.overdue++;
      else if (diffDays === 0) counts.dueToday++;
      else if (diffDays <= 2) counts.dueSoon++;
      else counts.upcoming++;
    });

    return counts;
  };

  const statusCounts = getStatusCounts();
  const totalEstimatedTime = exercises?.reduce((sum, ex) => sum + ex?.estimatedMinutes, 0);
  const completedTime = exercises?.filter(ex => ex?.completed)?.reduce((sum, ex) => sum + ex?.estimatedMinutes, 0);

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Week {currentWeek} Overview
          </h2>
          <div className="text-sm text-muted-foreground">
            Week {currentWeek} of {totalWeeks}
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {/* Progress Ring */}
        <div className="flex items-center justify-center">
          <div className="relative w-24 h-24">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-muted"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - completionPercentage / 100)}`}
                className="text-primary transition-all duration-500 ease-in-out"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xl font-bold text-foreground">
                  {completionPercentage}%
                </div>
                <div className="text-xs text-muted-foreground">
                  Complete
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="text-lg font-semibold text-foreground">
              {completedCount}/{totalCount}
            </div>
            <div className="text-xs text-muted-foreground">
              Exercises Done
            </div>
          </div>
          
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="text-lg font-semibold text-foreground">
              {formatTime(completedTime)}
            </div>
            <div className="text-xs text-muted-foreground">
              Time Invested
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Exercise Status</h3>
          
          {statusCounts?.completed > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-muted-foreground">Completed</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                {statusCounts?.completed}
              </span>
            </div>
          )}

          {statusCounts?.overdue > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <span className="text-sm text-muted-foreground">Overdue</span>
              </div>
              <span className="text-sm font-medium text-error">
                {statusCounts?.overdue}
              </span>
            </div>
          )}

          {statusCounts?.dueToday > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-warning" />
                <span className="text-sm text-muted-foreground">Due Today</span>
              </div>
              <span className="text-sm font-medium text-warning">
                {statusCounts?.dueToday}
              </span>
            </div>
          )}

          {statusCounts?.dueSoon > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-warning" />
                <span className="text-sm text-muted-foreground">Due Soon</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                {statusCounts?.dueSoon}
              </span>
            </div>
          )}

          {statusCounts?.upcoming > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upcoming</span>
              </div>
              <span className="text-sm font-medium text-foreground">
                {statusCounts?.upcoming}
              </span>
            </div>
          )}
        </div>

        {/* Time Summary */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">
                Total Time This Week
              </span>
            </div>
            <span className="text-sm font-semibold text-primary">
              {formatTime(totalEstimatedTime)}
            </span>
          </div>
          {totalEstimatedTime > completedTime && (
            <div className="mt-2 text-xs text-muted-foreground">
              {formatTime(totalEstimatedTime - completedTime)} remaining
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyOverview;